<template>
  <span
    class="station-row-icons flex flex-nowrap items-center"
    :class="max === undefined ? 'gap-0.5' : 'gap-px'"
    data-testid="station-row-icons"
  >
    <template v-for="icon in visibleIcons" :key="icon.key">
      <component
        :is="icon.component"
        v-if="icon.component"
        class="h-4 w-4 shrink-0"
        :title="icon.label"
        :aria-label="icon.label"
        :data-testid="icon.testId ?? `station-row-icon-${icon.key}`"
      />
      <v-icon
        v-else
        :icon="icon.path"
        :color="icon.color"
        size="16"
        class="shrink-0"
        :title="icon.label"
        :aria-label="icon.label"
        :data-testid="icon.testId ?? `station-row-icon-${icon.key}`"
      />
    </template>
    <span
      v-if="hiddenIcons.length"
      class="inline-flex h-4 min-w-[16px] shrink-0 items-center justify-center rounded bg-black/10 px-0.5 text-[10px] font-bold leading-none text-gray-700"
      :title="hiddenLabels"
      :aria-label="`Mais ${hiddenIcons.length}: ${hiddenLabels}`"
      data-testid="station-row-icons-more"
      >+{{ hiddenIcons.length }}</span
    >
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { StationStatusIcon } from './useStationRowStyle'

/**
 * Ícones de status das variantes `stack`/`row` do card (celular v2). Mesma lista, condições e
 * ordem do desktop (`statusIcons` do `useStationRowStyle`). Sem `v-tooltip`: no toque não há
 * hover e o tooltip pesa em ~280 cards por semana (spec §8.4, plano B); `title`/`aria-label`
 * mantêm o rótulo. O ramo `default` do `withIcon` NÃO usa este componente (desktop literal).
 *
 * v2.2: sempre numa linha só (sem quebra). `max` = quantos espaços de 16 px cabem; acima dele
 * mostra `max - 1` ícones e um "+N" no último espaço, com os rótulos escondidos no title/aria.
 * Com `max` o gap cai para 1 px (3 × 16 + 2 × 1 = 50 px cabe no iPhone SE). Sem `max`, mostra
 * todos com o gap de antes (comportamento anterior).
 */
const props = defineProps<{ icons: StationStatusIcon[]; max?: number }>()

const overflows = computed(() => props.max !== undefined && props.icons.length > props.max)
const visibleIcons = computed(() =>
  overflows.value ? props.icons.slice(0, Math.max(0, (props.max ?? 0) - 1)) : props.icons
)
const hiddenIcons = computed(() => props.icons.slice(visibleIcons.value.length))
const hiddenLabels = computed(() => hiddenIcons.value.map((icon) => icon.label).join(', '))
</script>
